// src/components/TrainerDashboard.jsx
import React, { useState, useEffect } from 'react';
import { LogOut, CheckCircle, Users } from 'lucide-react';
import { trainerAPI, adminAPI } from '../services/api';
import { getAuthData } from '../utils/auth';

const TrainerDashboard = ({ onLogout }) => {
  const { userId, schoolId, fullName } = getAuthData();
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [markedStudents, setMarkedStudents] = useState(new Set()); // Track students with attendance marked today
  const [attendanceForm, setAttendanceForm] = useState({
    attendanceDate: new Date().toISOString().split('T')[0],
    classNumber: 1,
    distanceKm: 4,
    present: true,
    remarks: ''
  });

  useEffect(() => {
    fetchBatches();
  }, []);

  // Fetch students when batch is selected
  useEffect(() => {
    if (selectedBatch) {
      fetchStudentsByBatch(selectedBatch.id);
    } else {
      setStudents([]);
    }
  }, [selectedBatch]);

  const fetchBatches = async () => {
    try {
      console.log('Fetching batches for trainer ID:', userId);
      const data = await trainerAPI.getBatchesByTrainer(userId);
      console.log('Fetched batches:', data);
      setBatches(data);
    } catch (err) {
      console.error('Error fetching batches:', err);
    }
  };

  const fetchStudentsByBatch = async (batchId) => {
    try {
      console.log('Fetching students for batch:', batchId);
      const data = await trainerAPI.getStudentsByBatch(batchId);
      console.log('Fetched students:', data);

      // Only include students with valid IDs
      const validStudents = data.filter(s => {
        const isValid = s.id;
        if (!isValid) {
          console.warn('Filtered out invalid student:', s);
        }
        return isValid;
      });

      console.log('Valid students count:', validStudents.length);
      setStudents(validStudents);
    } catch (err) {
      console.error('Error fetching students:', err);
    }
  };

  // No filtering needed - students are already filtered by batch from backend
  const filteredStudents = students;

  const markAttendance = async () => {
    if (!selectedBatch) {
      alert('Please select a batch first');
      return;
    }

    if (!selectedStudent) {
      alert('Please select a student first');
      return;
    }

    try {
      console.log('Marking attendance for student:', {
        studentId: selectedStudent.id,
        userId: selectedStudent.userId,
        batchId: selectedBatch.id
      });

      await trainerAPI.markAttendance({
        ...attendanceForm,
        studentId: selectedStudent.id,
        batchId: selectedBatch.id,
        schoolId,
        trainerId: userId
      });

      // Mark this student as having attendance for today
      setMarkedStudents(prev => new Set(prev).add(selectedStudent.id));

      alert('Attendance marked successfully!');
      setAttendanceForm({
        attendanceDate: new Date().toISOString().split('T')[0],
        classNumber: 1,
        distanceKm: 4,
        present: true,
        remarks: ''
      });
      setSelectedStudent(null);
    } catch (err) {
      console.error('Attendance error:', err);
      alert('Error marking attendance: ' + err.message);
    }
  };

  // Check if attendance button should be disabled
  const isAttendanceMarked = selectedStudent && markedStudents.has(selectedStudent.id);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-green-600 text-white p-4 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Trainer Dashboard</h1>
            <p className="text-green-100 text-sm">Welcome, {fullName}</p>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-4 py-2 bg-green-700 rounded-lg hover:bg-green-800 transition"
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </nav>

      <div className="container mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Mark Attendance Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="text-green-600" size={24} />
              <h2 className="text-xl font-bold text-gray-800">Mark Attendance</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Batch <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedBatch?.id || ''}
                  onChange={(e) => {
                    const batch = batches.find(b => b.id === parseInt(e.target.value));
                    setSelectedBatch(batch || null);
                    setSelectedStudent(null); // Reset student selection when batch changes
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                >
                  <option value="">Choose a batch</option>
                  {batches.map(batch => (
                    <option key={batch.id} value={batch.id}>
                      {batch.batchName} ({batch.currentStudents || 0}/{batch.maxStudents || 4} students)
                    </option>
                  ))}
                </select>
                {batches.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">No batches assigned to you</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Student <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedStudent?.id || ''}
                  onChange={(e) => setSelectedStudent(filteredStudents.find(s => s.id === parseInt(e.target.value)))}
                  disabled={!selectedBatch}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {!selectedBatch ? 'Select a batch first' : 'Choose a student'}
                  </option>
                  {filteredStudents.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.name || student.fullName || `Student ${student.userId}`}
                      {markedStudents.has(student.id) && ' ✓'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={attendanceForm.attendanceDate}
                  onChange={(e) => setAttendanceForm({...attendanceForm, attendanceDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Class Number (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={attendanceForm.classNumber}
                  onChange={(e) => setAttendanceForm({...attendanceForm, classNumber: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distance (km)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={attendanceForm.distanceKm}
                  onChange={(e) => setAttendanceForm({...attendanceForm, distanceKm: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="present"
                  checked={attendanceForm.present}
                  onChange={(e) => setAttendanceForm({...attendanceForm, present: e.target.checked})}
                  className="w-4 h-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="present" className="text-sm font-medium text-gray-700">
                  Present
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Remarks
                </label>
                <textarea
                  value={attendanceForm.remarks}
                  onChange={(e) => setAttendanceForm({...attendanceForm, remarks: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  rows="3"
                  placeholder="Any additional notes..."
                />
              </div>

              <button
                onClick={markAttendance}
                disabled={isAttendanceMarked}
                className={`w-full px-4 py-3 rounded-lg font-medium transition ${
                  isAttendanceMarked
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isAttendanceMarked ? 'Attendance Already Marked ✓' : 'Mark Attendance'}
              </button>
            </div>
          </div>

          {/* Students List Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="text-blue-600" size={24} />
              <h2 className="text-xl font-bold text-gray-800">
                Students List {selectedBatch && `- ${selectedBatch.batchName}`}
              </h2>
            </div>

            {!selectedBatch ? (
              <p className="text-center text-gray-500 py-8">
                Please select a batch to view students
              </p>
            ) : filteredStudents.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                No students in this batch
              </p>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredStudents.map(student => {
                  const isMarked = markedStudents.has(student.id);
                  return (
                    <div
                      key={student.id}
                      className={`p-4 border rounded-lg cursor-pointer transition ${
                        selectedStudent?.id === student.id
                          ? 'border-green-500 bg-green-50'
                          : isMarked
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedStudent(student)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-800">
                              {student.name || student.fullName || `Student ${student.userId}`}
                            </p>
                            {isMarked && (
                              <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
                                Marked ✓
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            Phone: {student.mobile || student.phone || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-600">
                            Age: {student.age} | Blood: {student.bloodGroup || 'N/A'}
                          </p>
                        </div>
                        {selectedStudent?.id === student.id && (
                          <CheckCircle className="text-green-600" size={24} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainerDashboard;